import React, { useState, useMemo, useCallback, useDeferredValue } from "react";
import { useQuery, useInfiniteQuery } from "@tanstack/react-query";
import { useLocation, useNavigate } from "react-router";
import QUERY_KEYS from "../constants/queryKeys";
import {
  handleGetAllAnnouncementsByDateService,
  handleGetAllAnnouncementsService,
  handleGetAnnouncementsByIdsService,
  handleGetAnnouncementStatsService,
  handleGetBriefGroupAnnouncementsService,
} from "../services/operations/announcements";
import moment from "moment";
import { classifyAnnouncementForDate, Event, getDepartmentFilterParams } from "../utils";
import { handleGetShiftsService } from "../services/shifts";
import { handleGetChecklistByShiftIDService } from "../services/operations/checklist";
import { useClientsContext, SubBranchData } from "./clients/ClientsContextProvider";
import AnnouncementView from "../pages/operations/components/Modals/AnnouncementView";

export type DailyBriefGroupType = "department" | "announcementType";
export type AnnouncementFilterLocation = {
  label: string;
  nodeType: "client" | "branch" | "subBranch";
  clientId: string;
  branchId?: string;
  subBranchId?: string;
} | null;

type DaySummaryAnnouncementFilter = {
  announcementIds: string[];
  category?: string;
  label?: string;
  clearLabel?: string;
  clientId?: string | null;
  branchId?: string | null;
} | null;

interface OperationsContextType {
  // General
  showDailyBriefScreen: boolean;
  setShowDailyBriefScreen: React.Dispatch<React.SetStateAction<boolean>>;
  showCalendarScreen: boolean;
  setShowCalendarScreen: React.Dispatch<React.SetStateAction<boolean>>;
  openAddEditAnnouncementDialog: boolean;
  setOpenAddEditAnnouncementDialog: React.Dispatch<React.SetStateAction<boolean>>;
  selectedAnnouncementForEdit: any;
  setSelectedAnnouncementForEdit: React.Dispatch<React.SetStateAction<any>>;
  announcementModalMode: "create" | "edit" | "add-task";
  setAnnouncementModalMode: React.Dispatch<
    React.SetStateAction<"create" | "edit" | "add-task">
  >;
  openAddCheckListDialog: boolean;
  setOpenAddCheckListDialog: React.Dispatch<React.SetStateAction<boolean>>;

  // Announcement
  filterSidebarVariant: "full" | "compact" | "hidden";
  setFilterSidebarVariant: React.Dispatch<
    React.SetStateAction<"full" | "compact" | "hidden">
  >;
  compactSidebarWidth: string;
  setCompactSidebarWidth: React.Dispatch<React.SetStateAction<string>>;
  announcementFilterType: string;
  setAnnouncementFilterType: React.Dispatch<React.SetStateAction<string>>;
  announcementFilterLocationLabel: string;
  setAnnouncementFilterLocationLabel: React.Dispatch<React.SetStateAction<string>>;
  announcementFilterLocation: AnnouncementFilterLocation;
  setAnnouncementFilterLocation: React.Dispatch<
    React.SetStateAction<AnnouncementFilterLocation>
  >;
  daySummaryAnnouncementFilter: DaySummaryAnnouncementFilter;
  clearDaySummaryAnnouncementFilter: () => void;
  announcementSearchText: string;
  setAnnouncementSearchText: React.Dispatch<React.SetStateAction<string>>;
  expandAnnouncement: boolean;
  setExpandAnnouncement: React.Dispatch<React.SetStateAction<boolean>>;
  expandAnnouncementIndex: number | null;
  setExpandAnnouncementIndex: React.Dispatch<React.SetStateAction<number | null>>;
  announcementData: any;
  regulationData: any;
  selectedAnnouncementId: string | null;
  setSelectedAnnouncementId: React.Dispatch<React.SetStateAction<string | null>>;
  selectedAnnouncementIds: Set<string>;
  toggleAnnouncementSelection: (announcementId: string) => void;
  clearAnnouncementSelection: () => void;

  // API data
  apiAnnouncementsData: any;
  apiAnnouncementsCalendarData: any;
  announcementStatsData: any;

  // Infinite scroll
  fetchNextPage: () => void;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;

  // Daily Brief
  expandDailyBriefSections: string[];
  setExpandDailyBriefSections: React.Dispatch<React.SetStateAction<string[]>>;

  // Calendar
  calendarDisplayType: "daily" | "scheduled";
  setCalendarDisplayType: React.Dispatch<React.SetStateAction<"daily" | "scheduled">>;
  calendarSelectedDate: Date;
  setCalendarSelectedDate: React.Dispatch<React.SetStateAction<Date>>;
  calendarData: any;

  briefSelectedDate: Date;
  setBriefSelectedDate: React.Dispatch<React.SetStateAction<Date>>;

  briefData: any;

  shiftTimeList: any;

  selectedShift: string;
  setSelectedShift: React.Dispatch<React.SetStateAction<string>>;

  newChecklistId: string | null;
  setNewChecklistId: React.Dispatch<React.SetStateAction<string | null>>;

  checklists: any;
  announcementUrgentData: any;

  announcementTypeFilter: string;
  setAnnouncementTypeFilter: React.Dispatch<React.SetStateAction<string>>;
  groupedAnnouncementByType: Record<string, number> | null;

  showAnnouncementViewModal: boolean;
  setShowAnnouncementViewModal: React.Dispatch<React.SetStateAction<boolean>>;

  selectedCalendarEvent: any;
  setSelectedCalendarEvent: React.Dispatch<React.SetStateAction<any>>;

  dailyBriefGroupBy: DailyBriefGroupType;
  setDailyBriefGroupBy: React.Dispatch<React.SetStateAction<DailyBriefGroupType>>;
}

const OperationsContext = React.createContext<OperationsContextType | null>(null);

type Props = {
  children: React.ReactNode;
};

function OperationsContextProvider({ children }: Props) {
  const location = useLocation();
  const navigate = useNavigate();
  const [user] = useState(() => {
    const s = localStorage.getItem("user");
    return s && s !== "undefined" ? JSON.parse(s) : null;
  });

  const { selectedClient, selectedLocation } = useClientsContext();

  // Derive the parent branch ID (for filtering) and the sub-branch ID when applicable.
  // When a sub-branch is selected, selectedBranchId is the PARENT branch so APIs
  // receive the correct branchId, and selectedSubBranchId carries the sub-branch.
  const selectedBranchId = useMemo(() => {
    if (!selectedLocation || selectedLocation.isClient) return undefined;
    if (selectedLocation.isBranch) return selectedLocation.id;
    return (selectedLocation as SubBranchData).branchId;
  }, [selectedLocation]);

  const selectedSubBranchId = useMemo(() => {
    if (selectedLocation?.isSubBranch) return selectedLocation.id;
    return undefined;
  }, [selectedLocation]);

  // General
  const [showDailyBriefScreen, setShowDailyBriefScreen] = React.useState(true);
  const [showCalendarScreen, setShowCalendarScreen] = React.useState(true);
  const [openAddEditAnnouncementDialog, setOpenAddEditAnnouncementDialog] =
    useState<boolean>(false);
  const [selectedAnnouncementForEdit, setSelectedAnnouncementForEdit] =
    useState<any>(null);
  const [announcementModalMode, setAnnouncementModalMode] = useState<
    "create" | "edit" | "add-task"
  >("create");
  const [openAddCheckListDialog, setOpenAddCheckListDialog] = useState<boolean>(false);

  const [showAnnouncementViewModal, setShowAnnouncementViewModal] = useState(false);
  const [selectedCalendarEvent, setSelectedCalendarEvent] = useState<any>(null);

  // Announcement
  const [filterSidebarVariant, setFilterSidebarVariant] = React.useState<
    "full" | "compact" | "hidden"
  >("full");
  const [compactSidebarWidth, setCompactSidebarWidth] = React.useState("66px");
  const [announcementFilterType, setAnnouncementFilterType] =
    React.useState<string>("all");
  const [announcementFilterLocationLabel, setAnnouncementFilterLocationLabel] =
    React.useState<string>("");
  const [announcementFilterLocation, setAnnouncementFilterLocation] =
    React.useState<AnnouncementFilterLocation>(null);
  const [announcementSearchText, setAnnouncementSearchText] = React.useState("");
  const deferredAnnouncementSearch = useDeferredValue(announcementSearchText.trim());
  const [expandAnnouncement, setExpandAnnouncement] = React.useState(false);
  const [expandAnnouncementIndex, setExpandAnnouncementIndex] = React.useState<
    number | null
  >(null);
  const [announcementData, setAnnouncementData] = React.useState<any>([]);
  const [regulationData, setRegulationData] = React.useState<any>([]);
  const [announcementUrgentData, setAnnouncementUrgentData] = React.useState<any>([]);
  const [newChecklistId, setNewChecklistId] = React.useState<string | null>(null);
  const [announcementTypeFilter, setAnnouncementTypeFilter] = React.useState<string>("");
  const [groupedAnnouncementByType, setGroupedAnnouncementByType] = React.useState<Record<
    string,
    number
  > | null>(null);
  const [selectedAnnouncementId, setSelectedAnnouncementId] = useState<string | null>(
    null,
  );
  const [selectedAnnouncementIds, setSelectedAnnouncementIds] = useState<Set<string>>(
    () => new Set(),
  );

  const daySummaryAnnouncementFilter = useMemo<DaySummaryAnnouncementFilter>(() => {
    const state = location.state as any;
    const validAnnouncementIds: string[] = Array.isArray(state?.daySummaryAnnouncementIds)
      ? state.daySummaryAnnouncementIds.filter(
          (id: unknown): id is string => typeof id === "string" && !!id,
        )
      : [];
    const announcementIds = Array.from(new Set<string>(validAnnouncementIds));

    if (announcementIds.length === 0) return null;

      return {
        announcementIds,
        category: state?.daySummaryCategory,
        label: state?.daySummaryLabel,
        clearLabel: state?.daySummaryClearLabel,
        clientId: state?.daySummaryClientId,
        branchId: state?.daySummaryBranchId,
      };
  }, [location.state]);

  const clearDaySummaryAnnouncementFilter = useCallback(() => {
    navigate("/operations", { replace: true, state: null });
  }, [navigate]);

  const toggleAnnouncementSelection = useCallback((announcementId: string) => {
    setSelectedAnnouncementIds((prev) => {
      const next = new Set(prev);
      if (next.has(announcementId)) next.delete(announcementId);
      else next.add(announcementId);
      return next;
    });
  }, []);

  const clearAnnouncementSelection = useCallback(() => {
    setSelectedAnnouncementIds((prev) => (prev.size === 0 ? prev : new Set()));
  }, []);

  /* the selection belongs to the list the user is looking at, so it does not
     survive a change of filter, type, search or location */
  React.useEffect(() => {
    clearAnnouncementSelection();
  }, [
    announcementFilterType,
    announcementTypeFilter,
    deferredAnnouncementSearch,
    announcementFilterLocation,
    clearAnnouncementSelection,
  ]);

  React.useEffect(() => {
    setAnnouncementFilterType("all");
    setAnnouncementFilterLocationLabel(selectedLocation?.name ?? "");
    setAnnouncementFilterLocation(
      selectedLocation
        ? {
            label: selectedLocation.name,
            nodeType: selectedLocation.isClient
              ? "client"
              : selectedLocation.isBranch
                ? "branch"
                : "subBranch",
            clientId: selectedLocation.isClient
              ? selectedLocation.id
              : selectedLocation.clientId,
            ...(selectedLocation.isBranch && {
              branchId: selectedLocation.id,
            }),
            ...(selectedLocation.isSubBranch && {
              branchId: (selectedLocation as SubBranchData).branchId,
              subBranchId: selectedLocation.id,
            }),
          }
        : null,
    );
  }, [
    selectedClient?._id,
    selectedBranchId,
    selectedSubBranchId,
    selectedLocation,
    selectedLocation?.name,
  ]);

  const announcementFilterClientId =
    announcementFilterLocation?.clientId ?? selectedClient?._id;
  const announcementFilterBranchId =
    announcementFilterLocation?.nodeType === "branch"
      ? announcementFilterLocation.branchId
      : announcementFilterLocation?.nodeType === "subBranch"
        ? announcementFilterLocation.branchId
        : undefined;
  const announcementFilterSubBranchId =
    announcementFilterLocation?.nodeType === "subBranch"
      ? announcementFilterLocation.subBranchId
      : undefined;
  const announcementFilterLocationKey = [
    announcementFilterClientId,
    announcementFilterBranchId,
    announcementFilterSubBranchId,
  ].join(":");

  // Daily Brief
  const [expandDailyBriefSections, setExpandDailyBriefSections] = React.useState<
    string[]
  >([]);

  const [dailyBriefGroupBy, setDailyBriefGroupBy] =
    React.useState<DailyBriefGroupType>("department");

  // Calendar
  const [calendarDisplayType, setCalendarDisplayType] = React.useState<
    "daily" | "scheduled"
  >("daily");
  const [calendarSelectedDate, setCalendarSelectedDate] = React.useState(new Date());
  const [briefSelectedDate, setBriefSelectedDate] = React.useState(new Date());
  const [calendarData, setCalendarData] = React.useState<any>([]);
  const [briefData, setBriefData] = React.useState([]);
  const [shiftTimeList, setShiftTimeList] = React.useState([]);
  const [selectedShift, setSelectedShift] = React.useState("");

  React.useEffect(() => {
    setSelectedCalendarEvent(null);
    setSelectedShift("");
    setShiftTimeList([]);
    setCalendarData(
      calendarDisplayType === "daily"
        ? { single: [], allDay: [] }
        : { byDate: {}, recurring: [], allDayByDate: {} },
    );
  }, [announcementFilterLocationKey, calendarDisplayType]);

  const ANNOUNCEMENTS_PAGE_LIMIT = 50;

  const getAnnouncementDepartmentParam = useCallback(() => {
    if (announcementFilterType === "all") {
      return getDepartmentFilterParams();
    }
    return { department: announcementFilterType };
  }, [announcementFilterType]);

  const daySummaryAnnouncementIdsKey =
    daySummaryAnnouncementFilter?.announcementIds.join(":") ?? "";

  // Stats query
  const { data: announcementStatsData } = useQuery({
    queryKey: [
      QUERY_KEYS.GET_ALL_ANNOUNCEMENTS_FOR_STATS,
      announcementFilterClientId,
      announcementFilterBranchId,
      announcementFilterSubBranchId,
      user?.departmentId,
    ],
    queryFn: () =>
      handleGetAnnouncementStatsService({
        clientId: announcementFilterClientId,
        ...(announcementFilterBranchId && {
          branchId: announcementFilterBranchId,
        }),
        ...(announcementFilterSubBranchId && {
          subBranchId: announcementFilterSubBranchId,
        }),
        ...getDepartmentFilterParams(),
      }),
    refetchInterval: 2 * 60 * 1000,
    enabled: !!announcementFilterClientId,
  });

  // API Query for announcements (infinite scroll)
  const {
    data: apiAnnouncementsInfiniteData,
    fetchNextPage,
    hasNextPage = false,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: [
      daySummaryAnnouncementFilter
        ? QUERY_KEYS.GET_ANNOUNCEMENTS_BY_IDS
        : QUERY_KEYS.GET_ALL_ANNOUNCEMENTS,
      announcementFilterClientId,
      announcementFilterBranchId,
      announcementFilterSubBranchId,
      user?.departmentId,
      announcementFilterType,
      announcementTypeFilter,
      deferredAnnouncementSearch,
      daySummaryAnnouncementIdsKey,
    ],
    queryFn: ({ pageParam }) => {
      if (daySummaryAnnouncementFilter) {
        return handleGetAnnouncementsByIdsService({
          announcementIds: daySummaryAnnouncementFilter.announcementIds,
          page: pageParam,
          limit: ANNOUNCEMENTS_PAGE_LIMIT,
          ...(daySummaryAnnouncementFilter.clientId
            ? {
                clientId: daySummaryAnnouncementFilter.clientId,
              }
            : {}),
          ...(daySummaryAnnouncementFilter.branchId && {
            branchId: daySummaryAnnouncementFilter.branchId,
          }),
          ...getAnnouncementDepartmentParam(),
          ...(announcementTypeFilter && {
            announcementType: announcementTypeFilter,
          }),
          ...(deferredAnnouncementSearch && {
            search: deferredAnnouncementSearch,
          }),
        });
      }

      return handleGetAllAnnouncementsService({
        page: pageParam,
        limit: ANNOUNCEMENTS_PAGE_LIMIT,
        clientId: announcementFilterClientId,
        ...(announcementFilterBranchId && {
          branchId: announcementFilterBranchId,
        }),
        ...(announcementFilterSubBranchId && {
          subBranchId: announcementFilterSubBranchId,
        }),
        ...getAnnouncementDepartmentParam(),
        ...(announcementTypeFilter && {
          announcementType: announcementTypeFilter,
        }),
        ...(deferredAnnouncementSearch && {
          search: deferredAnnouncementSearch,
        }),
      });
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      if (typeof lastPage?.pagination?.hasNext === "boolean") {
        return lastPage.pagination.hasNext ? allPages.length + 1 : undefined;
      }

      const lastPageData = lastPage?.data || [];
      if (lastPageData.length < ANNOUNCEMENTS_PAGE_LIMIT) return undefined;
      return allPages.length + 1;
    },
    refetchInterval: 2 * 60 * 1000,
    enabled: daySummaryAnnouncementFilter
      ? daySummaryAnnouncementFilter.announcementIds.length > 0
      : !!announcementFilterClientId,
  });

  const apiAnnouncementsData = useMemo(() => {
    if (!apiAnnouncementsInfiniteData?.pages) return null;
    return {
      data: apiAnnouncementsInfiniteData.pages.flatMap((page: any) => page?.data || []),
    };
  }, [apiAnnouncementsInfiniteData]);

  const { data: apiAnnouncementsCalendarData } = useQuery({
    queryKey: [
      QUERY_KEYS.GET_ALL_ANNOUNCEMENTS_BY_DATE,
      moment(calendarSelectedDate).format("YYYY-MM-DD"),
      announcementFilterClientId,
      announcementFilterBranchId,
      announcementFilterSubBranchId,
      user?.departmentId,
    ],
    queryFn: () =>
      handleGetAllAnnouncementsByDateService(
        moment(calendarSelectedDate).format("YYYY-MM-DD"),
        {
          clientId: announcementFilterClientId,
          ...(announcementFilterBranchId && {
            branchId: announcementFilterBranchId,
          }),
          ...(announcementFilterSubBranchId && {
            subBranchId: announcementFilterSubBranchId,
          }),
          ...getDepartmentFilterParams(),
        },
      ),
    refetchInterval: 2 * 60 * 1000,
    enabled: !!calendarSelectedDate && !!announcementFilterClientId,
  });

  // API Query for brief announcements
  // const {data: briefAnnouncementsData} = useQuery({
  //   queryKey: [
  //     QUERY_KEYS.GET_BRIEF_ANNOUNCEMENTS_BY_DATE,
  //     moment(briefSelectedDate).format('YYYY-MM-DD'),
  //     selectedClient?._id,
  //     selectedBranchId,
  //     user?.departmentId,
  //   ],
  //   queryFn: () =>
  //     handleGetBriefAnnouncementsService({
  //       byGroup: true,
  //       date: moment(briefSelectedDate).format('YYYY-MM-DD'),
  //       clientId: selectedClient?._id,
  //       ...(selectedBranchId && {branchId: selectedBranchId}),
  //       ...getDepartmentFilterParams(),
  //     }),
  //   refetchInterval: 2 * 60 * 1000,
  //   enabled: !!briefSelectedDate && !!selectedClient?._id,
  // });
  const { data: briefAnnouncementsData } = useQuery({
    queryKey: [
      QUERY_KEYS.GET_BRIEF_ANNOUNCEMENTS_BY_DATE,
      moment(briefSelectedDate).format("YYYY-MM-DD"),
      announcementFilterClientId,
      announcementFilterBranchId,
      announcementFilterSubBranchId,
      dailyBriefGroupBy,
      user?.departmentId,
    ],
    queryFn: () =>
      handleGetBriefGroupAnnouncementsService(
        moment(briefSelectedDate).format("YYYY-MM-DD"),
        {
          groupBy: dailyBriefGroupBy,
          clientId: announcementFilterClientId,
          ...(announcementFilterBranchId && {
            branchId: announcementFilterBranchId,
          }),
          ...(announcementFilterSubBranchId && {
            subBranchId: announcementFilterSubBranchId,
          }),
          ...getDepartmentFilterParams(),
        },
      ),
    refetchInterval: 2 * 60 * 1000,
    enabled: !!briefSelectedDate && !!announcementFilterClientId,
  });

  const { data: shifts } = useQuery({
    queryKey: [
      QUERY_KEYS.GET_ALL_SHIFTS,
      announcementFilterClientId,
      announcementFilterBranchId,
      announcementFilterSubBranchId,
    ],
    queryFn: () =>
      handleGetShiftsService({
        ...(announcementFilterClientId && {
          clientId: announcementFilterClientId,
        }),
        ...(announcementFilterBranchId && {
          branchId: [announcementFilterBranchId],
        }),
        ...(announcementFilterSubBranchId && {
          subBranchId: announcementFilterSubBranchId,
        }),
      }),
    enabled: !!announcementFilterClientId,
  });

  const { data: checklists } = useQuery({
    queryKey: [
      QUERY_KEYS.GET_ALL_CHECKLISTS_SHIFT_ID,
      selectedShift,
      announcementFilterClientId,
      announcementFilterBranchId,
      announcementFilterSubBranchId,
      user?.departmentId,
    ],
    queryFn: () => {
      let ids;
      if (selectedShift === "all")
        ids = (shiftTimeList as any)?.shifts?.map((shift: any) => shift._id) || [];
      else ids = [selectedShift];

      return handleGetChecklistByShiftIDService({
        shiftIds: ids,
        ...(announcementFilterClientId && {
          clientId: announcementFilterClientId,
        }),
        ...(announcementFilterBranchId && {
          branchIds: [announcementFilterBranchId],
        }),
        ...(announcementFilterSubBranchId && {
          subBranchId: announcementFilterSubBranchId,
        }),
        ...getDepartmentFilterParams(),
      });
    },
    enabled: !!selectedShift && !!announcementFilterClientId,
  });

  React.useEffect(() => {
    if (!shifts?.data) return;
    setShiftTimeList(shifts?.data);
  }, [shifts]);

  React.useEffect(() => {
    if (!briefAnnouncementsData?.data) return;
    setBriefData(briefAnnouncementsData?.data);
  }, [briefAnnouncementsData]);
  // Populate type counts from stats API
  React.useEffect(() => {
    if (!announcementStatsData?.data?.byType) {
      setGroupedAnnouncementByType(null);
      return;
    }
    const grouped = announcementStatsData.data.byType.reduce(
      (acc: Record<string, number>, item: any) => {
        acc[item.type] = item.count;
        return acc;
      },
      {},
    );
    setGroupedAnnouncementByType(grouped);
  }, [announcementStatsData]);

  // Process API data when it changes
  React.useEffect(() => {
    const allAnnouncements = apiAnnouncementsData?.data;
    if (!allAnnouncements || allAnnouncements.length === 0) {
      setAnnouncementData([]);
      setRegulationData([]);
      setAnnouncementUrgentData([]);
      return;
    }

    // Transform API data to match the expected structure
    const transformedAnnouncements = allAnnouncements.map((announcement: any) => ({
      ...announcement,
      id: announcement._id,
      title: announcement.announcementTitle,
      description: announcement.description,
      read: announcement.readStatus?.isRead || false,
      presentInBrief: announcement.showInBrief || false,
      tasks:
        announcement.tasks?.map((task: any, index: number) => ({
          ...task,
          id: task._id || `task-${announcement._id}-${index}`,
          title: task.taskTitle,
          description: task.taskTitle,
          taskStatus: "open",
          doneTime: task.completedAt
            ? new Date(task.completedAt).toLocaleDateString("he-IL")
            : "",
          canAddComments: true,
          recipient: {
            assignedTo:
              task.recipients?.length > 0
                ? `${task.recipients[0].firstName} ${task.recipients[0].lastName}`
                : "לא הוקצה",
          },
        })) || [],
      taskStatus: "משימות",
      checklist: announcement.checklists || [],
      time: {
        from: announcement.createdAt
          ? moment(announcement.createdAt).local().format("HH:mm DD/MM/YYYY")
          : "",
        to: "",
        occurrence: "daily",
      },
    }));

    // Search is applied server-side via `search` query param.
    const regular = transformedAnnouncements.filter(
      (a: any) => a.announcementType !== "Regulation" && a.isUrgent !== true,
    );

    const urgent = transformedAnnouncements.filter(
      (a: any) => a.announcementType !== "Regulation" && a.isUrgent === true,
    );

    const regulation = transformedAnnouncements.filter(
      (a: any) => a.announcementType === "Regulation",
    );

    setAnnouncementData(regular);
    setRegulationData(regulation);
    setAnnouncementUrgentData(urgent);
  }, [apiAnnouncementsData]);

  React.useEffect(() => {
    if (!apiAnnouncementsCalendarData?.data) return;
    const anns: any[] = apiAnnouncementsCalendarData?.data ?? [];

    if (calendarDisplayType === "daily") {
      const single: Event[] = [];
      const allDay: Event[] = [];

      anns.forEach((a) => {
        const result = classifyAnnouncementForDate(a, calendarSelectedDate, "daily");
        if (!result.bucket || !result.event) return;

        if (result.bucket === "single") single.push(result.event);
        if (result.bucket === "allDay") allDay.push(result.event);
      });

      single.sort((a, b) => (a.start ?? "").localeCompare(b.start ?? ""));

      setCalendarData({ single, allDay });
      return;
    } else {
      const byDate: Record<string, Event[]> = {};
      const allDayByDate: Record<string, Event[]> = {};
      const recurring: Event[] = [];

      anns.forEach((a) => {
        const result = classifyAnnouncementForDate(a, calendarSelectedDate, "scheduled");
        if (!result.bucket || !result.event) return;

        if (result.bucket === "recurring") {
          recurring.push(result.event);
          return;
        }

        const k = result.event.date;

        if (result.bucket === "allDay") {
          (allDayByDate[k] ||= []).push(result.event);
        } else {
          (byDate[k] ||= []).push(result.event);
        }
      });

      recurring.sort((a, b) => (a.start ?? "").localeCompare(b.start ?? ""));
      Object.keys(byDate).forEach((k) => {
        byDate[k].sort((a, b) => (a.start ?? "").localeCompare(b.start ?? ""));
      });

      setCalendarData({ byDate, recurring, allDayByDate });
    }
  }, [apiAnnouncementsCalendarData, calendarDisplayType, calendarSelectedDate]);

  return (
    <OperationsContext.Provider
      value={{
        // General
        showDailyBriefScreen,
        setShowDailyBriefScreen,
        showCalendarScreen,
        setShowCalendarScreen,
        openAddEditAnnouncementDialog,
        setOpenAddEditAnnouncementDialog,
        selectedAnnouncementForEdit,
        setSelectedAnnouncementForEdit,
        announcementModalMode,
        setAnnouncementModalMode,
        openAddCheckListDialog,
        setOpenAddCheckListDialog,

        // Announcement
        filterSidebarVariant,
        setFilterSidebarVariant,
        compactSidebarWidth,
        setCompactSidebarWidth,
        announcementFilterType,
        setAnnouncementFilterType,
        announcementFilterLocationLabel,
        setAnnouncementFilterLocationLabel,
        announcementFilterLocation,
        setAnnouncementFilterLocation,
        daySummaryAnnouncementFilter,
        clearDaySummaryAnnouncementFilter,
        announcementSearchText,
        setAnnouncementSearchText,
        expandAnnouncement,
        setExpandAnnouncement,
        expandAnnouncementIndex,
        setExpandAnnouncementIndex,
        announcementData,
        regulationData,
        apiAnnouncementsData,
        apiAnnouncementsCalendarData,
        announcementStatsData,
        announcementUrgentData,
        selectedAnnouncementId,
        setSelectedAnnouncementId,
        selectedAnnouncementIds,
        toggleAnnouncementSelection,
        clearAnnouncementSelection,

        // Infinite scroll
        fetchNextPage,
        hasNextPage: hasNextPage ?? false,
        isFetchingNextPage,

        // Daily Brief
        expandDailyBriefSections,
        setExpandDailyBriefSections,
        briefData,

        dailyBriefGroupBy,
        setDailyBriefGroupBy,

        // Calendar
        calendarDisplayType,
        setCalendarDisplayType,
        calendarSelectedDate,
        setCalendarSelectedDate,
        calendarData,
        briefSelectedDate,
        setBriefSelectedDate,

        shiftTimeList,
        selectedShift,
        setSelectedShift,
        checklists,

        newChecklistId,
        setNewChecklistId,

        announcementTypeFilter,
        setAnnouncementTypeFilter,
        groupedAnnouncementByType,

        showAnnouncementViewModal,
        setShowAnnouncementViewModal,

        selectedCalendarEvent,
        setSelectedCalendarEvent,
      }}
    >
      {children}
      <AnnouncementView
        open={showAnnouncementViewModal}
        onClose={() => {
          setShowAnnouncementViewModal(false);
          setSelectedAnnouncementId(null);
        }}
      />
    </OperationsContext.Provider>
  );
}

export default OperationsContextProvider;

export const useOperationsContext = () => {
  const context = React.useContext(OperationsContext);
  if (context === null) {
    throw new Error(
      "useOperationsContext must be used within an OperationsContextProvider",
    );
  }
  return context;
};
